import React, { useEffect, useState, createRef, useRef } from 'react';
import { SectionLayout } from 'components/Sections/SectionLayout';
import RichText from 'components/RichText';
import WebsiteImage from 'components/WebsiteImage';
import richTextOptions from '../InPageFeaturesV3/InPageFeaturesV3FeaturedContent/richTextOptions';
import FeaturedContent from './FeaturedContent';
import useBreakpointView from 'utils/useBreakpointView';
import { STIcon } from 'components/Brand/Icons/STIcon';
import { Container, StyledTitle, StyledButton } from './styles';
import { twMerge } from 'tailwind-merge';

const InPageFeaturesV3New = (props) => {
  const { index, inPageNavigationItems, title, toggleComponent } = props;
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAtTop, setIsAtTop] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
  const linkRefs = inPageNavigationItems ? inPageNavigationItems?.map(() => createRef()) : [];
  const scrollRef = useRef(null);
  const dropdownRef = useRef(null);
  const isMobile = useBreakpointView(['xs', 'sm']);

  const checkTopBottom = () => {
    if (!scrollRef.current) {
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const atTop = Math.floor(scrollTop) === 0;
    const atBottom = scrollHeight - Math.ceil(scrollTop) <= clientHeight;
    setIsAtTop(atTop);
    setIsAtBottom(atBottom);
  };
  useEffect(() => {
    const scrollRefElement = scrollRef.current;
    if (!scrollRefElement) return;

    const handleScroll = () => {
      checkTopBottom();
    };

    scrollRefElement.addEventListener('scroll', handleScroll);

    return () => {
      scrollRefElement.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    checkTopBottom();

    window.addEventListener('resize', checkTopBottom);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', checkTopBottom);
    };
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry], obs) => {
        if (entry.isIntersecting) {
          checkTopBottom();
          obs.disconnect();
        }
      },
      { root: null },
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const top =
      activeIndex < 2
        ? 0
        : linkRefs[activeIndex]?.current?.offsetTop - scrollRef?.current?.offsetTop;
    scrollRef?.current?.scrollTo({ top: top, behavior: 'smooth' });
  }, [activeIndex]);

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsMobileDropdownOpen(false);
    }
  };

  const handleMoveToTop = () => {
    scrollRef?.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleMoveToDown = () => {
    scrollRef?.current?.scrollTo({ top: scrollRef?.current?.scrollHeight, behavior: 'smooth' });
  };

  const handleMobileDropdownItemClick = (idx) => {
    setActiveIndex(idx);
    setIsMobileDropdownOpen(false);
  };

  const showTitle = title && !toggleComponent;

  return (
    <Container id="in-page-features-v3-new" className="in-page-features-v3-new">
      {showTitle && <StyledTitle>{title}</StyledTitle>}
      <SectionLayout
        innerContainerStyles={twMerge(
          'in-page-features-v3--inner-container',
          showTitle && 'title-included',
          toggleComponent && 'toggle-component',
        )}
      >
        <div className="flex flex-col md:flex-row md:gap-[32px]">
          {!isMobile && (
            <div className="max-h-[724px] w-[30%] xl:min-w-[324px] relative">
              {!isAtTop && (
                <div
                  className={`absolute w-full h-[30px] bg-white border border-solid border-[#737475] z-20 flex items-center justify-center cursor-pointer top-0`}
                  onClick={handleMoveToTop}
                  onKeyDown={(e) => onKeyDown(e, () => handleMoveToTop())}
                >
                  <STIcon className="rotate-180" type="icon-select-arrow" />
                </div>
              )}
              <div className="no-scrollbar h-full overflow-y-scroll" ref={scrollRef}>
                <div className="flex w-full flex-col gap-[10px] py-px pb-[10px] md:flex-wrap">
                  {inPageNavigationItems?.map((item, idx) => (
                    <div
                      className={twMerge(
                        'flex flex-col p-[15px] w-full border-[1px] border-solid cursor-pointer transition-all duration-500 ease-in-out relative',
                        activeIndex !== idx ? 'border-transparent' : 'border-[#17191C4D]',
                      )}
                      ref={linkRefs[idx]}
                      onClick={() => setActiveIndex(idx)}
                      key={idx}
                    >
                      <div className="flex items-center gap-[10px] relative">
                        <WebsiteImage {...item.icon} widthDesktop="30px" />
                        <p className="text-[18px] font-extrabold leading-[120%] m-0">
                          {item.title}
                        </p>
                      </div>
                      <div
                        className={twMerge(
                          'overflow-hidden mt-[10px]',
                          activeIndex !== idx ? 'max-h-0' : 'max-h-[500px]',
                        )}
                      >
                        <RichText content={item.description} options={richTextOptions} />
                        {item.link && (
                          <StyledButton type="secondary" to={item.link}>
                            <p>Learn More</p>
                          </StyledButton>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {!isAtBottom && (
                <div
                  className={`absolute w-full h-[30px] bg-white border border-solid border-[#737475] z-20 flex items-center justify-center cursor-pointer bottom-0`}
                  onClick={handleMoveToDown}
                  onKeyDown={(e) => onKeyDown(e, () => handleMoveToDown())}
                >
                  <STIcon type="icon-select-arrow" />
                </div>
              )}
            </div>
          )}
          {isMobile && (
            <div ref={dropdownRef} className="relative">
              <div
                className={
                  'flex items-center gap-[10px] relative p-[10px] border-[1px] border-solid border-[var(--darkest)]'
                }
                onClick={() => setIsMobileDropdownOpen(!isMobileDropdownOpen)}
              >
                <WebsiteImage {...inPageNavigationItems?.[activeIndex]?.icon} widthDesktop="32px" />
                <p className={`text-[18px] font-extrabold leading-[120%] m-0`}>
                  {inPageNavigationItems?.[activeIndex]?.title}
                </p>
                <STIcon type="icon-select-arrow" className={`absolute right-[12px] top-[20px]`} />
              </div>
              <div
                className={twMerge(
                  'w-full absolute z-20 max-h-[350px] p-[10px_5px] border bg-white border-solid border-[var(--darkest)] mt-[-1px]',
                  isMobileDropdownOpen ? 'flex' : 'hidden',
                )}
              >
                <div className="w-full overflow-x-hidden overflow-y-scroll">
                  <div className="flex w-full flex-col gap-[10px]">
                    {inPageNavigationItems?.map((item, idx) =>
                      activeIndex !== idx ? (
                        <div
                          className={'flex items-center gap-[15px] relative p-[10px_5px] h-[52px]'}
                          onClick={() => handleMobileDropdownItemClick(idx)}
                          key={idx}
                        >
                          <WebsiteImage {...item.icon} widthDesktop="30px" />
                          <p className={`text-[18px] font-extrabold leading-[120%] m-0`}>
                            {item?.title}
                          </p>
                        </div>
                      ) : null,
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="p-[10px] pb-[15px] md:px-[80px] md:py-[60px] md:bg-[#DFE0E1] w-full max-h-full md:max-h-[724px] border-[1px] border-solid border-[#17191C66] border-t-0 md:border-none">
            {isMobile && (
              <div className="mb-[10px]">
                <RichText
                  content={inPageNavigationItems?.[activeIndex]?.description}
                  options={richTextOptions}
                />
              </div>
            )}
            <FeaturedContent item={inPageNavigationItems?.[activeIndex]} />
            {isMobile && inPageNavigationItems?.[activeIndex]?.link && (
              <StyledButton type="secondary" to={inPageNavigationItems?.[activeIndex]?.link}>
                <p>Learn More</p>
              </StyledButton>
            )}
          </div>
        </div>
      </SectionLayout>
    </Container>
  );
};

export default InPageFeaturesV3New;
